import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import {
  trigger,
  state,
  style,
  transition,
  animate,
  keyframes,
} from '@angular/animations';

import { ListingsService, UserListing } from '../services/listings.service';
import { AuthService } from '../services/auth.service';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import {
  VehicleSelectorComponent,
  VehicleSelection,
} from '../vehicle-selector/vehicle-selector.component';
import { MotorcycleBrandSelectorComponent } from '../vehicle-selector/motorcycle-brand-selector.component';
import { LocationAutocompleteComponent } from '../location-autocomplete/location-autocomplete.component';
import { LocationResult } from '../services/location.service';
import { getFormConfig, FormField } from '../utils/form-fields-configs';
import { environment } from '../../environments/environment';

interface ImageItem {
  id: string;
  url: string;
  isNew: boolean;
  file?: File;
  previewUrl?: string;
}

interface DateFieldConfig {
  yearRange: {
    start: number;
    end: number;
  };
  maxDate: Date;
}

interface PricingPlan {
  name: string;
  price: number;
  color: string;
  gradient: string;
  features: {
    listingDuration: number;
    imagesAllowed: number;
    descriptionLength: number;
    appearsHigherInSearch: boolean;
    listingInFeaturedSection: boolean;
    listingInSuggestedItems: boolean;
    emailNotificationToSubscribers: boolean;
    freeRelist: boolean;
  };
  popular: boolean;
  displayFeatures: {
    imagesAllowed: string;
    descriptionLength: string;
    appearsHigherInSearch: string;
    listingInFeaturedSection: string;
    listingInSuggestedItems: string;
    emailNotificationToSubscribers: string;
    freeRelist: string;
  };
}

@Component({
  selector: 'app-edit-listing',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    VehicleSelectorComponent,
    MotorcycleBrandSelectorComponent,
    LocationAutocompleteComponent,
  ],
  templateUrl: './edit-listing.component.html',
  styleUrl: './edit-listing.component.css',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('250ms ease-out', style({ opacity: 0 }))]),
    ]),
    trigger('slideInUp', [
      transition(':enter', [
        animate(
          '400ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          keyframes([
            style({
              opacity: 0,
              transform: 'translateY(60px) scale(0.95)',
              offset: 0,
            }),
            style({
              opacity: 0.5,
              transform: 'translateY(30px) scale(0.98)',
              offset: 0.3,
            }),
            style({
              opacity: 1,
              transform: 'translateY(0) scale(1)',
              offset: 1,
            }),
          ])
        ),
      ]),
      transition(':leave', [
        animate(
          '250ms ease-in',
          keyframes([
            style({
              opacity: 1,
              transform: 'translateY(0) scale(1)',
              offset: 0,
            }),
            style({
              opacity: 0,
              transform: 'translateY(-30px) scale(0.95)',
              offset: 1,
            }),
          ])
        ),
      ]),
    ]),
  ],
})
export class EditListingComponent implements OnInit, OnDestroy {
  listingForm!: FormGroup;
  listing: UserListing | null = null;
  formFields: FormField[] = [];
  loading = true;
  error: string | null = null;
  isSubmitting = false;
  existingImages: string[] = [];
  imagesToDelete: string[] = [];
  currentPlanMaxImages = 7;
  fieldSections: { [key: string]: FormField[] } = {};
  allImages: ImageItem[] = [];
  draggedItem: ImageItem | null = null;
  draggedIndex: number = -1;
  dropZoneActive: boolean = false;
  showSuccessModal: boolean = false;

  private dateFieldsConfig: { [fieldName: string]: DateFieldConfig } = {
    first_registration: {
      yearRange: { start: 1950, end: new Date().getFullYear() },
      maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    },
    registered_until: {
      yearRange: {
        start: new Date().getFullYear(),
        end: new Date().getFullYear() + 1,
      },
      maxDate: new Date(new Date().getFullYear() + 1, new Date().getMonth(), 1),
    },
  };

  dateFieldsData: {
    [key: string]: {
      month: string;
      year: string;
    };
  } = {};

  dateFieldYearOptions: { [fieldName: string]: number[] } = {};

  monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  private readonly plans: PricingPlan[] = [
    {
      name: 'Free',
      price: 0,
      color: '#6c757d',
      gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
      features: {
        listingDuration: 7,
        imagesAllowed: 7,
        descriptionLength: 300,
        appearsHigherInSearch: false,
        listingInFeaturedSection: false,
        listingInSuggestedItems: false,
        emailNotificationToSubscribers: false,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '7 Images',
        descriptionLength: '300 Characters',
        appearsHigherInSearch: 'No',
        listingInFeaturedSection: 'No',
        listingInSuggestedItems: 'No',
        emailNotificationToSubscribers: 'No',
        freeRelist: 'No',
      },
      popular: false,
    },
    {
      name: 'Starter',
      price: 1.99,
      color: '#3498db',
      gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
      features: {
        listingDuration: 14,
        imagesAllowed: 14,
        descriptionLength: 400,
        appearsHigherInSearch: true,
        listingInFeaturedSection: false,
        listingInSuggestedItems: false,
        emailNotificationToSubscribers: false,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '14 Images',
        descriptionLength: '400 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'No',
        listingInSuggestedItems: 'No',
        emailNotificationToSubscribers: 'No',
        freeRelist: 'No',
      },
      popular: false,
    },
    {
      name: 'Pro',
      price: 2.99,
      color: '#9b59b6',
      gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
      features: {
        listingDuration: 21,
        imagesAllowed: 21,
        descriptionLength: 500,
        appearsHigherInSearch: true,
        listingInFeaturedSection: true,
        listingInSuggestedItems: false,
        emailNotificationToSubscribers: true,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '21 Images',
        descriptionLength: '500 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'Yes',
        listingInSuggestedItems: 'No',
        emailNotificationToSubscribers: 'Yes',
        freeRelist: 'No',
      },
      popular: true,
    },
    {
      name: 'Elite',
      price: 3.99,
      color: '#e74c3c',
      gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
      features: {
        listingDuration: 42,
        imagesAllowed: 42,
        descriptionLength: 5000,
        appearsHigherInSearch: true,
        listingInFeaturedSection: true,
        listingInSuggestedItems: true,
        emailNotificationToSubscribers: true,
        freeRelist: true,
      },
      displayFeatures: {
        imagesAllowed: '42 Images',
        descriptionLength: '5000 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'Yes',
        listingInSuggestedItems: 'Yes',
        emailNotificationToSubscribers: 'Yes',
        freeRelist: 'Yes (1 Free)',
      },
      popular: false,
    },
  ];

  private subscriptions: Subscription[] = [];
  private isUpdatingPowerFields = false;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private http: HttpClient,
    private listingsService: ListingsService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initAllDateFieldYearOptions();
    const listingId = this.route.snapshot.paramMap.get('id');
    if (listingId) {
      this.loadListing(listingId);
    } else {
      this.handleError('Invalid listing ID');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.showSuccessModal) {
      document.body.style.overflow = 'auto';
    }
  }

  trackByImageId(index: number, item: ImageItem): string {
    return item.id;
  }

  trackBySection(index: number, item: any): string {
    return item.key;
  }

  trackByField(index: number, item: FormField): string {
    return item.name;
  }

  trackByOption(index: number, item: any): string {
    return item.value;
  }

  private handleError(message: string): void {
    this.error = message;
    this.loading = false;
  }

  private loadListing(listingId: string): void {
    this.loading = true;
    this.error = null;

    if (!this.authService.isLoggedIn()) {
      this.handleError('You must be logged in to edit listings');
      return;
    }

    const currentUser = this.authService.getCurrentUserSync();
    if (!currentUser) {
      this.handleError('Unable to verify user authentication');
      return;
    }

    const userListingsSub = this.listingsService
      .getAllUserListingsSorted()
      .subscribe({
        next: (userListings) => {
          const userListing = userListings.find(
            (listing) => listing.id.toString() === listingId
          );

          if (userListing) {
            const subcategory =
              this.listingsService.getSubcategoryFromTableName(
                userListing.table_name
              );
            this.loadListingDetails(listingId, subcategory);
          } else {
            this.handleError(
              'Listing not found or you do not have permission to edit it'
            );
          }
        },
        error: (error) => {
          console.error('Error loading user listings:', error);
          this.handleError('Failed to load listing information');
        },
      });

    this.subscriptions.push(userListingsSub);
  }

  private loadListingDetails(listingId: string, subcategory: string): void {
    const detailsSub = this.listingsService
      .getListingDetails(listingId, subcategory)
      .subscribe({
        next: (listing) => {
          this.listing = listing;
          this.setupFormFields();
          this.setupImages();
          this.initializeForm();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading listing details:', error);
          this.handleError('Failed to load listing details');
        },
      });

    this.subscriptions.push(detailsSub);
  }

  private setupFormFields(): void {
    if (!this.listing?.table_name) return;

    try {
      const category = this.listingsService.getCategoryFromTableName(
        this.listing.table_name
      );
      const subcategory = this.listingsService.getSubcategoryFromTableName(
        this.listing.table_name
      );

      const categoryMap: { [key: string]: string } = {
        'Real Estate': 'real-estate',
        Vehicles: 'vehicles',
        Marketplace: 'marketplace',
        Jobs: 'jobs',
      };

      const formCategory = categoryMap[category] || category.toLowerCase();
      this.formFields = getFormConfig(formCategory, subcategory);
      this.organizeFieldSections();
    } catch (error) {
      console.error('Error setting up form fields:', error);
      this.formFields = [];
    }
  }

  private setupImages(): void {
    this.allImages = [];

    if (this.listing?.plan_name) {
      this.currentPlanMaxImages = this.getPlanImageLimit(
        this.listing.plan_name
      );
    } else {
      this.currentPlanMaxImages = 7;
    }

    if (this.listing?.images && Array.isArray(this.listing.images)) {
      this.existingImages = [...this.listing.images];
      this.listing.images.forEach((imageUrl, index) => {
        this.allImages.push({
          id: `existing-${index}`,
          url: imageUrl,
          isNew: false,
        });
      });
    } else if (this.listing?.first_image) {
      this.existingImages = [this.listing.first_image];
      this.allImages.push({
        id: 'existing-0',
        url: this.listing.first_image,
        isNew: false,
      });
    }
  }

  private getPlanImageLimit(planName: string): number {
    const planLimits: { [key: string]: number } = {
      Free: 7,
      Starter: 14,
      Pro: 21,
      Elite: 42,
    };
    return planLimits[planName] || 7;
  }

  private getPlanDescriptionLimit(planName: string): number {
    const planLimits: { [key: string]: number } = {
      Free: 300,
      Starter: 400,
      Pro: 500,
      Elite: 5000,
    };
    return planLimits[planName] || 300;
  }

  get maxDescriptionLength(): number {
    return this.getPlanDescriptionLimit(this.listing?.plan_name || 'Free');
  }

  getDescriptionLength(): number {
    return (this.listingForm.get('description')?.value || '').length;
  }

  getDescriptionPlanValidationError(): string | null {
    const descriptionLength = this.getDescriptionLength();
    const currentPlan = this.listing?.plan_name || 'Free';
    const currentPlanLimit = this.maxDescriptionLength;

    if (descriptionLength > currentPlanLimit) {
      return `Maximum ${currentPlanLimit} characters allowed for ${currentPlan} plan. Please reduce description length.`;
    }

    if (descriptionLength > currentPlanLimit * 0.8) {
      const remainingCharacters = currentPlanLimit - descriptionLength;
      if (remainingCharacters > 0) {
        return `You can add ${remainingCharacters} more characters with your ${currentPlan} plan.`;
      }
    }

    return null;
  }

  private validateDescriptionLength(): void {
    if (this.error && this.error.includes('characters')) {
      this.error = null;
    }
  }

  onDescriptionChange(): void {
    this.validateDescriptionLength();
  }

  private organizeFieldSections(): void {
    this.fieldSections = {
      'Basic Information': [],
      Pricing: [],
      'Location & Contact': [],
      Details: [],
      'Additional Information': [],
    };

    this.formFields.forEach((field) => {
      if (['title', 'description'].includes(field.name)) {
        this.fieldSections['Basic Information'].push(field);
      } else if (
        field.name.includes('price') ||
        field.name.includes('cost') ||
        field.name.includes('rent') ||
        field.name === 'currency'
      ) {
        this.fieldSections['Pricing'].push(field);
      } else if (
        ['location', 'address', 'city', 'postal_code', 'country'].includes(
          field.name
        )
      ) {
        this.fieldSections['Location & Contact'].push(field);
      } else if (
        [
          'brand',
          'model',
          'year',
          'condition',
          'fuel',
          'transmission',
          'vehicle',
        ].includes(field.name)
      ) {
        this.fieldSections['Details'].push(field);
      } else {
        this.fieldSections['Additional Information'].push(field);
      }
    });

    Object.keys(this.fieldSections).forEach((key) => {
      if (this.fieldSections[key].length === 0) {
        delete this.fieldSections[key];
      }
    });
  }

  private initializeForm(): void {
    const formControls: { [key: string]: any } = {};

    this.formFields.forEach((field) => {
      const existingValue = this.getExistingFieldValue(field.name);
      const validators = this.getFieldValidators(field);

      if (field.disabled) {
        formControls[field.name] = [
          { value: existingValue, disabled: true },
          validators,
        ];
      } else {
        formControls[field.name] = [existingValue, validators];
      }
    });

    this.listingForm = this.formBuilder.group(formControls);
    this.setupPowerFieldsConversion();
  }

  private setupPowerFieldsConversion(): void {
    const powerKwControl = this.listingForm.get('power_kw');
    const powerHpControl = this.listingForm.get('power_hp');

    if (powerKwControl && powerHpControl) {
      powerKwControl.valueChanges.subscribe((value) => {
        if (!this.isUpdatingPowerFields && value !== null && value !== '') {
          this.isUpdatingPowerFields = true;
          const hp = this.convertKwToHp(parseFloat(value));
          if (!isNaN(hp)) {
            powerHpControl.setValue(Math.round(hp), { emitEvent: false });
          }
          this.isUpdatingPowerFields = false;
        }
      });

      powerHpControl.valueChanges.subscribe((value) => {
        if (!this.isUpdatingPowerFields && value !== null && value !== '') {
          this.isUpdatingPowerFields = true;
          const kw = this.convertHpToKw(parseFloat(value));
          if (!isNaN(kw)) {
            powerKwControl.setValue(Math.round(kw), { emitEvent: false });
          }
          this.isUpdatingPowerFields = false;
        }
      });
    }
  }

  private convertKwToHp(kw: number): number {
    return kw * 1.35962162;
  }

  private convertHpToKw(hp: number): number {
    return hp * 0.73549875;
  }

  private getFieldValidators(field: FormField): any[] {
    const validators: any[] = [];

    if (field.validators && field.validators.length > 0) {
      validators.push(...field.validators);
    }

    if (field.required) {
      const hasRequired = validators.some((v) => v === Validators.required);
      if (!hasRequired) {
        validators.push(Validators.required);
      }
    }

    if (field.type === 'email') {
      const hasEmailValidator = validators.some((v) => v === Validators.email);
      if (!hasEmailValidator) {
        validators.push(Validators.email);
      }
    }

    if (field.type === 'number') {
      if (field.min !== undefined) {
        const minValue = field.min;
        const hasMinValidator = validators.some(
          (v) =>
            v === Validators.min(minValue) || (v && v.min && v.min === minValue)
        );
        if (!hasMinValidator) {
          validators.push(Validators.min(minValue));
        }
      }
      if (field.max !== undefined) {
        const maxValue = field.max;
        const hasMaxValidator = validators.some(
          (v) =>
            v === Validators.max(maxValue) || (v && v.max && v.max === maxValue)
        );
        if (!hasMaxValidator) {
          validators.push(Validators.max(maxValue));
        }
      }
    }

    return validators;
  }

  private getExistingFieldValue(fieldName: string): any {
    if (!this.listing) return this.getDefaultFieldValue(fieldName);

    if (fieldName === 'vehicle') {
      const listingData = this.listing as any;
      const brand = listingData['brand'];
      const model = listingData['model'];

      if (brand || model) {
        return {
          brand: brand || '',
          model: model || '',
        };
      }

      return { brand: '', model: '' };
    }

    const value = (this.listing as any)[fieldName];

    if (value === null || value === undefined || value === '') {
      return this.getDefaultFieldValue(fieldName);
    }

    if (this.isCustomDropdownDateField(fieldName)) {
      if (value) {
        const match = value.match(/^(\d{2})\/(\d{4})$/);
        if (match) {
          const month = match[1];
          const year = match[2];

          this.dateFieldsData[fieldName] = {
            month: parseInt(month, 10).toString(),
            year: year,
          };

          return value;
        }
      }
      return '';
    }

    if (this.isDateField(fieldName)) {
      if (value) {
        const date = new Date(value);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value;
    }

    return value ? value.toString() : '';
  }

  private isCustomDropdownDateField(fieldName: string): boolean {
    return (
      fieldName === 'first_registration' || fieldName === 'registered_until'
    );
  }

  private isDateField(fieldName: string): boolean {
    if (this.isCustomDropdownDateField(fieldName)) {
      return false;
    }

    return (
      fieldName.includes('date') ||
      fieldName === 'available_from' ||
      fieldName === 'move_in_date' ||
      fieldName === 'start_availability'
    );
  }

  private getDefaultFieldValue(fieldName: string): any {
    const field = this.formFields.find((f) => f.name === fieldName);

    if (!field) return '';

    switch (field.type) {
      case 'checkbox':
        return false;
      case 'number':
        return null;
      case 'vehicle-selector':
        return { brand: '', model: '' };
      case 'location':
        return '';
      default:
        return '';
    }
  }

  getFieldDescribedBy(fieldName: string): string | null {
    const parts: string[] = [];

    if (this.formFields.find((f) => f.name === fieldName)?.hint) {
      parts.push(`${fieldName}-hint`);
    }

    if (this.isFieldInvalid(fieldName)) {
      parts.push(`${fieldName}-error`);
    }

    return parts.length > 0 ? parts.join(' ') : null;
  }

  getImageCount(): number {
    return this.allImages.length;
  }

  getImagePlanValidationError(): string | null {
    const imageCount = this.getImageCount();
    const currentPlan = this.listing?.plan_name || 'Free';
    const currentPlanLimit = this.currentPlanMaxImages;

    if (imageCount > currentPlanLimit) {
      return `Maximum ${currentPlanLimit} images allowed for ${currentPlan} plan. Please remove some images.`;
    }

    if (imageCount > currentPlanLimit * 0.8) {
      const remainingImages = currentPlanLimit - imageCount;
      if (remainingImages > 0) {
        return `You can add ${remainingImages} more images with your ${currentPlan} plan.`;
      }
    }

    return null;
  }

  get maxImages(): number {
    return this.currentPlanMaxImages;
  }

  private validateImageCount(): void {
    const imageCount = this.getImageCount();

    if (imageCount > this.currentPlanMaxImages) {
      this.error = `Maximum ${this.currentPlanMaxImages} images allowed for ${
        this.listing?.plan_name || 'Free'
      } plan. Please remove some images.`;
      return;
    }

    if (this.error && this.error.includes('images')) {
      this.error = null;
    }
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;

    const files = Array.from(target.files);
    const totalImages = this.allImages.length + files.length;

    if (totalImages > this.currentPlanMaxImages) {
      this.error = `Maximum ${this.currentPlanMaxImages} images allowed for ${
        this.listing?.plan_name || 'Free'
      } plan. You can add ${
        this.currentPlanMaxImages - this.allImages.length
      } more images.`;
      return;
    }

    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        if (file.size <= 20 * 1024 * 1024) {
          this.processImageFile(file, index);
        } else {
          this.error = `Image ${file.name} is too large. Maximum size is 20MB.`;
        }
      }
    });

    target.value = '';
  }

  private processImageFile(file: File, index: number): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const newImage: ImageItem = {
        id: `new-${Date.now()}-${index}`,
        url: e.target.result,
        isNew: true,
        file: file,
        previewUrl: e.target.result,
      };
      this.allImages.push(newImage);
      this.validateImageCount();
    };
    reader.readAsDataURL(file);
  }

  removeImage(imageItem: ImageItem): void {
    const index = this.allImages.findIndex((img) => img.id === imageItem.id);
    if (index > -1) {
      if (!imageItem.isNew) {
        this.imagesToDelete.push(imageItem.url);
      }
      this.allImages.splice(index, 1);
      this.validateImageCount();
    }
  }

  onDragStart(event: DragEvent, imageItem: ImageItem, index: number): void {
    this.draggedItem = imageItem;
    this.draggedIndex = index;
    this.dropZoneActive = true;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', imageItem.id);
    }

    const target = event.target as HTMLElement;
    target.classList.add('dragging');
  }

  onDragEnd(event: DragEvent): void {
    this.draggedItem = null;
    this.draggedIndex = -1;
    this.dropZoneActive = false;

    const target = event.target as HTMLElement;
    target.classList.remove('dragging');

    const allItems = document.querySelectorAll('.image-item');
    allItems.forEach((item) => item.classList.remove('drag-over'));
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    const target = event.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  }

  onDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();

    if (
      this.draggedItem &&
      this.draggedIndex !== -1 &&
      this.draggedIndex !== dropIndex
    ) {
      const draggedItem = this.allImages.splice(this.draggedIndex, 1)[0];
      this.allImages.splice(dropIndex, 0, draggedItem);
    }

    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
    this.onDragEnd(event);
  }

  onLocationSelected(location: LocationResult): void {}

  get canSave(): boolean {
    const title = this.listingForm.get('title')?.value;
    const description = this.listingForm.get('description')?.value;
    const descriptionLength = this.getDescriptionLength();

    const hasEssentialData =
      title && title.trim() && description && description.trim();

    const isDescriptionValid = descriptionLength <= this.maxDescriptionLength;

    const isImagesValid = this.getImageCount() <= this.currentPlanMaxImages;

    return (
      this.listingForm.valid &&
      hasEssentialData &&
      isDescriptionValid &&
      isImagesValid
    );
  }

  onSubmit(): void {
    if (this.isSubmitting) return;

    this.markFormGroupTouched();

    if (this.listingForm.invalid) {
      this.error = 'Please fix the errors in the form before submitting.';
      setTimeout(() => {
        const firstError = document.querySelector('.field-error, .is-invalid');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    const imageCount = this.getImageCount();
    if (imageCount > this.currentPlanMaxImages) {
      this.error = `Maximum ${this.currentPlanMaxImages} images allowed for ${
        this.listing?.plan_name || 'Free'
      } plan. Please remove some images.`;
      return;
    }

    const descriptionLength = this.getDescriptionLength();
    if (descriptionLength > this.maxDescriptionLength) {
      return;
    }

    this.error = null;
    this.isSubmitting = true;

    try {
      const updateData = this.buildUpdateData();
      const newImageFiles = this.getNewImageFiles();

      if (newImageFiles.length > 0) {
        this.uploadNewImagesAndUpdate(updateData, newImageFiles);
      } else {
        this.updateListing(updateData);
      }
    } catch (error) {
      console.error('Error preparing form submission:', error);
      this.error = 'Failed to prepare form data. Please try again.';
      this.isSubmitting = false;
    }
  }

  private buildUpdateData(): any {
    const updateData: any = {};
    const listingData = this.listing as any;

    Object.keys(this.listingForm.value).forEach((key) => {
      const value = this.listingForm.value[key];

      if (key === 'vehicle' && typeof value === 'object') {
        const currentBrand = value.brand || '';
        const currentModel = value.model || '';
        const existingBrand = listingData?.['brand'] || '';
        const existingModel = listingData?.['model'] || '';

        if (currentBrand !== existingBrand || currentModel !== existingModel) {
          updateData.brand = currentBrand;
          updateData.model = currentModel;
        }
        return;
      }

      if (key !== 'vehicle') {
        const existingValue = this.getExistingFieldValue(key);
        const normalizedValue = this.normalizeValue(value);
        const normalizedExisting = this.normalizeValue(existingValue);

        if (normalizedValue !== normalizedExisting) {
          updateData[key] = value;
        }
      }
    });

    const remainingExistingImages = this.allImages
      .filter((img) => !img.isNew && !this.imagesToDelete.includes(img.url))
      .map((img) => img.url);

    updateData.images = remainingExistingImages;

    return updateData;
  }

  private getNewImageFiles(): File[] {
    return this.allImages
      .filter((img) => img.isNew && img.file)
      .map((img) => img.file!)
      .filter((file) => file);
  }

  private uploadNewImagesAndUpdate(updateData: any, newFiles: File[]): void {
    const formData = new FormData();
    newFiles.forEach((file) => {
      formData.append('images', file);
    });

    const uploadSub = this.http
      .post<any>(`${environment.apiUrl}/upload/images`, formData)
      .subscribe({
        next: (uploadResponse) => {
          if (uploadResponse.success && uploadResponse.urls) {
            const allOrderedImages = this.buildOrderedImageList(
              uploadResponse.urls
            );
            updateData.images = allOrderedImages;
            this.updateListing(updateData);
          } else {
            this.error = 'Failed to upload images';
            this.isSubmitting = false;
          }
        },
        error: (error) => {
          console.error('Error uploading images:', error);
          this.error = 'Failed to upload images. Please try again.';
          this.isSubmitting = false;
        },
      });

    this.subscriptions.push(uploadSub);
  }

  private buildOrderedImageList(uploadedUrls: string[]): string[] {
    const allOrderedImages: string[] = [];
    let newImageIndex = 0;

    this.allImages.forEach((img) => {
      if (img.isNew) {
        allOrderedImages.push(uploadedUrls[newImageIndex]);
        newImageIndex++;
      } else if (!this.imagesToDelete.includes(img.url)) {
        allOrderedImages.push(img.url);
      }
    });

    return allOrderedImages;
  }

  private normalizeValue(value: any): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    return value;
  }

  private updateListing(updateData: any): void {
    if (!this.listing) return;

    const updateSub = this.listingsService
      .updateUserListing(this.listing, updateData)
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.showSuccessMessage();
        },
        error: (error) => {
          console.error('Error updating listing:', error);
          this.error =
            error.message || 'Failed to update listing. Please try again.';
          this.isSubmitting = false;
        },
      });

    this.subscriptions.push(updateSub);
  }

  onModalOverlayClick(): void {
    this.goToMyListings();
  }

  private showSuccessMessage(): void {
    this.showSuccessModal = true;
    document.body.style.overflow = 'hidden';
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
    document.body.style.overflow = 'auto';
  }

  goToMyListings(): void {
    this.closeSuccessModal();
    this.router.navigate(['/my-listings']);
  }

  viewUpdatedListing(): void {
    this.closeSuccessModal();
    if (this.listing) {
      const subcategory = this.listingsService.getSubcategoryFromTableName(
        this.listing.table_name
      );
      this.router.navigate(['/listing-details', subcategory, this.listing.id]);
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.listingForm.controls).forEach((key) => {
      const control = this.listingForm.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.listingForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(field: FormField): string {
    const control = this.listingForm.get(field.name);
    if (!control || !control.errors) return '';

    const errors = control.errors;

    if (errors['required']) {
      return `${field.label} is required`;
    }
    if (errors['email']) {
      return 'Please enter a valid email address';
    }
    if (errors['minlength']) {
      return `${field.label} must be at least ${errors['minlength'].requiredLength} characters`;
    }
    if (errors['maxlength']) {
      return `${field.label} must not exceed ${errors['maxlength'].requiredLength} characters`;
    }
    if (errors['min']) {
      return `${field.label} must be at least ${errors['min'].min}`;
    }
    if (errors['max']) {
      return `${field.label} must not exceed ${errors['max'].max}`;
    }

    return 'Invalid value';
  }

  goBack(): void {
    this.location.back();
  }

  getCategoryName(): string {
    if (!this.listing?.table_name) return '';
    return this.listingsService.getCategoryFromTableName(
      this.listing.table_name
    );
  }

  getSubcategoryName(): string {
    if (!this.listing?.table_name) return '';
    return this.listingsService.getSubcategoryFromTableName(
      this.listing.table_name
    );
  }

  private initAllDateFieldYearOptions(): void {
    Object.keys(this.dateFieldsConfig).forEach((fieldName) => {
      this.dateFieldYearOptions[fieldName] =
        this.generateYearOptions(fieldName);
    });
  }

  private generateYearOptions(fieldName: string): number[] {
    const config = this.dateFieldsConfig[fieldName];
    if (!config) return [];

    const years: number[] = [];
    for (
      let year = config.yearRange.end;
      year >= config.yearRange.start;
      year--
    ) {
      years.push(year);
    }
    return years;
  }

  getYearOptionsForField(fieldName: string): number[] {
    return this.dateFieldYearOptions[fieldName] || [];
  }

  isMonthDisabled(monthValue: string, fieldName: string): boolean {
    const data = this.dateFieldsData[fieldName];
    const config = this.dateFieldsConfig[fieldName];

    if (!data || !data.year || !config) return false;

    const selectedYear = parseInt(data.year, 10);
    const selectedMonth = parseInt(monthValue, 10);
    const maxDate = config.maxDate;

    const selectedDate = new Date(selectedYear, selectedMonth - 1, 1);

    return selectedDate > maxDate;
  }

  onDateMonthChange(event: Event, fieldName: string): void {
    const select = event.target as HTMLSelectElement;

    if (!this.dateFieldsData[fieldName]) {
      this.dateFieldsData[fieldName] = { month: '', year: '' };
    }

    this.dateFieldsData[fieldName].month = select.value;
    this.updateDateValue(fieldName);
  }

  onDateYearChange(event: Event, fieldName: string): void {
    const select = event.target as HTMLSelectElement;

    if (!this.dateFieldsData[fieldName]) {
      this.dateFieldsData[fieldName] = { month: '', year: '' };
    }

    this.dateFieldsData[fieldName].year = select.value;

    const month = this.dateFieldsData[fieldName].month;
    if (month && this.isMonthDisabled(month, fieldName)) {
      this.dateFieldsData[fieldName].month = '';
      const monthSelect = document.getElementById(
        fieldName + '_month'
      ) as HTMLSelectElement;
      if (monthSelect) monthSelect.value = '';
    }

    this.updateDateValue(fieldName);
  }

  private updateDateValue(fieldName: string): void {
    const data = this.dateFieldsData[fieldName];
    if (!data) return;

    const { month, year } = data;

    if (month && year) {
      const paddedMonth = month.padStart(2, '0');
      const dateString = `${paddedMonth}/${year}`;
      this.listingForm.get(fieldName)?.setValue(dateString);
      this.listingForm.get(fieldName)?.markAsTouched();
    } else {
      this.listingForm.get(fieldName)?.setValue('');
    }
  }
}
